import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "../../../helpers/axios";

import { generatePublicUrl } from "../../../urlConfig";

import {
    getOfficeOrderById,
    updateOfficeOrderStatus,
} from "../../../actions/officeOrder.actions";

import {
    closeOfficeOrderModal,
    setOfficeOrderModalTab,
} from "../../../actions/officeOrderModal.actions";
import { openOfficeTaskCreateModal } from "../../../actions/officeTaskCreateModal.actions";
import { openOfficeEntityModal } from "../../../actions/officeEntityModal.actions";
import { openOfficeProductGallery } from "../../../actions/officeProductGallery.actions";
import { openOfficeProduct3D } from "../../../actions/officeProduct3D.actions";
import { getOfficeMembers } from "../../../actions/officeMember.actions";

import OrderPaymentEditor from "../pages/orders/OrderPaymentEditor";
import OrderStatusEditor from "../pages/orders/OrderStatusEditor";
import {
    formatDate,
    formatMoney,
    getOfficeId,
    getOrderId,
    Row,
} from "../pages/orders/orderModalCenter.utils";
import { emitOfficeAnalyticsRefresh } from "../../../utils/officeAnalyticsEvents";
import OrderFinanceCalendar from "./OrderFinanceCalendar";
import OrderDocumentPanel from "../documents/OrderDocumentPanel";
import "../../../scss/adminOffice/components/_global-order-modal-AO.scss";
import "../../../scss/adminOffice/components/order-payment-AO.scss";
import "../../../scss/adminOffice/components/order-status-AO.scss";
function getPortalTarget() {
    if (typeof document === "undefined") return null;

    return document.querySelector(".admin-app-AO") || document.body;
}

function getOrderNumber(order = {}) {
    return order?.orderNumber || order?.orderNumberAdditional || "Без номера";
}

function getOrderAmount(order = {}) {
    const discountForBuyers = Number(order?.discountForBuyers || 0);
    const totalAmount = Number(order?.totalAmount || 0);

    if (discountForBuyers > 0) return discountForBuyers;
    if (totalAmount > 0) return totalAmount;

    if (!Array.isArray(order?.items)) return 0;

    return order.items.reduce((sum, item) => {
        const price = Number(item?.bayerPrice || item?.payablePrice || item?.price || 0);
        const qty = Math.max(1, Number(item?.purchasedQty || 1));

        return sum + price * qty;
    }, 0);
}

function normalizePicture(value) {
    if (!value) return "";

    if (typeof value === "string") return value;

    return value.img || value.url || value.path || value.src || value.filename || "";
}

function pickPicture(array) {
    if (!Array.isArray(array) || !array.length) return "";

    const main = array.find((item) => item?.mainImg || item?.main || item?.isMain);
    const visible = array.find((item) => item?.visibleImg !== false);

    return normalizePicture(main || visible || array[0]);
}

function getProductPicture(product = {}, item = {}) {
    // Мини-карточки никогда не тянут оригиналы.
    // Полная галерея загружается отдельным запросом только по клику на лупу.
    return (
        normalizePicture(product?.reducedImage) ||
        normalizePicture(item?.reducedImage) ||
        normalizePicture(product?.thumbnail) ||
        normalizePicture(item?.thumbnail) ||
        ""
    );
}

function getProductTitle(item = {}) {
    const product = item?.productId || {};

    return (
        product?.fullName ||
        product?.name ||
        product?.article ||
        item?.fullNameProduct ||
        item?._id ||
        "Товар"
    );
}

function getProductId(item = {}) {
    const product = item?.productId;

    if (!product) return "";
    if (typeof product === "object") return product._id || product.id || "";

    return String(product);
}

function hasProductModel(item = {}) {
    const product = item?.productId && typeof item.productId === "object"
        ? item.productId
        : {};

    return product?.hasModel === true || !!product?.modelPath || item?.hasModel === true || !!item?.modelPath;
}

function getLastCompletedStatus(order = {}) {
    const statuses = Array.isArray(order?.orderStatus) ? order.orderStatus : [];

    if (!statuses.length) {
        return {
            type: "ordered",
            text: "Оформлен",
        };
    }

    const completed = statuses
        .filter((status) => status?.isCompleted)
        .sort((a, b) => {
            const aDate = a?.date ? new Date(a.date).getTime() : 0;
            const bDate = b?.date ? new Date(b.date).getTime() : 0;

            return bDate - aDate;
        });

    return completed[0] || statuses[0];
}

function getAddress(order = {}) {
    return order?.addressId || order?.address || {};
}

function getFiles(order = {}) {
    const result = [];

    const candidates = [
        order?.orderScreens,
        order?.screens,
        order?.files,
        order?.itemPictures,
        order?.attachments,
    ];

    candidates.forEach((list) => {
        if (!Array.isArray(list)) return;

        list.forEach((item) => {
            const file = normalizePicture(item);

            if (file) result.push(file);
        });
    });

    return result;
}

function getCustomerName(order = {}) {
    if (order?.user?.firstName || order?.user?.lastName) {
        return [order.user.firstName, order.user.lastName].filter(Boolean).join(" ");
    }

    return order?.user?.email || order?.createdByName || "—";
}

function OverviewTab({ order }) {
    const address = getAddress(order);
    const status = getLastCompletedStatus(order);

    return (
        <div className="global-order-overview-AO">
            <div className="global-order-card-AO">
                <div className="global-order-card-AO__title">Основное</div>

                <div className="global-order-card-AO__rows">
                    <Row k="Номер заказа" v={getOrderNumber(order)} />
                    <Row k="ID заказа" v={getOrderId(order)} />
                    <Row k="Создан" v={formatDate(order?.createdAt)} />
                    <Row k="Сумма заказа" v={formatMoney(getOrderAmount(order))} />
                    <Row k="Текущий статус" v={status?.text || "—"} />
                    <Row k="Тип заказа" v={order?.scope || (order?.officeId ? "office" : "personal")} />
                </div>
            </div>

            <div className="global-order-card-AO">
                <div className="global-order-card-AO__title">Клиент</div>

                <div className="global-order-card-AO__rows">
                    <Row k="Имя" v={getCustomerName(order)} />
                    <Row k="Email" v={order?.user?.email} />
                    <Row k="Телефон" v={address?.phone || order?.phone} />
                    <Row k="Тип покупателя" v={order?.buyerType} />
                </div>
            </div>

            <div className="global-order-card-AO global-order-card-AO--wide">
                <div className="global-order-card-AO__title">Адрес</div>

                <div className="global-order-card-AO__rows">
                    <Row k="Получатель" v={address?.fullName} />
                    <Row k="Город" v={address?.city} />
                    <Row
                        k="Адрес"
                        v={[address?.street, address?.house, address?.apartment].filter(Boolean).join(", ")}
                    />
                    <Row k="Комментарий" v={address?.comment} />
                </div>
            </div>
        </div>
    );
}

function ProductsTab({ order, onOpenProduct, onOpenGallery, onOpen3D }) {
    const items = Array.isArray(order?.items) ? order.items : [];

    if (!items.length) {
        return (
            <div className="global-order-empty-AO">
                В заказе нет товаров.
            </div>
        );
    }

    return (
        <div className="global-order-products-AO">
            {items.map((item, index) => {
                const product = item?.productId || {};
                const picture = getProductPicture(product, item);
                const productId = getProductId(item);
                const hasModel = hasProductModel(item);

                return (
                    <div
                        key={item?._id || productId || index}
                        className="global-order-products-AO__item"
                    >
                        <div
                            className="global-order-products-AO__img is-product-gallery"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (productId) onOpenGallery?.(productId, item);
                            }}
                            title={productId ? "Открыть фотографии товара" : undefined}
                        >
                            {picture ? (
                                <img
                                    src={generatePublicUrl(picture)}
                                    alt={getProductTitle(item)}
                                    loading="lazy"
                                    decoding="async"
                                    onError={(event) => {
                                        event.currentTarget.style.display = "none";
                                    }}
                                />
                            ) : (
                                <span>Нет фото</span>
                            )}

                            {productId ? (
                                <i className="product-gallery-trigger-AO__zoom" aria-hidden="true">⌕</i>
                            ) : null}
                        </div>

                        <div className="global-order-products-AO__body">
                            <div className="global-order-products-AO__title">
                                {getProductTitle(item)}
                            </div>

                            <div className="global-order-products-AO__meta">
                                <span>Кол-во: {item?.purchasedQty || 0}</span>
                                <span>Цена: {formatMoney(item?.bayerPrice || item?.payablePrice)}</span>
                                <span>
                                    Размер: {[item?.length, item?.width, item?.height].filter(Boolean).join(" × ") || "—"}
                                </span>
                                <span>ID: {productId || "—"}</span>
                            </div>

                            {productId ? (
                                <div className="global-order-products-AO__actions">
                                    <button
                                        type="button"
                                        className="global-order-products-AO__quick"
                                        onClick={() => onOpenProduct?.(productId, item)}
                                    >
                                        Быстрый просмотр
                                    </button>

                                    {hasModel ? (
                                        <button
                                            type="button"
                                            className="global-order-products-AO__quick is-3d"
                                            onClick={() => onOpen3D?.(productId, item)}
                                        >
                                            Открыть в 3D
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function DeliveryTab({ order }) {
    const address = getAddress(order);

    return (
        <div className="global-order-overview-AO">
            <div className="global-order-card-AO">
                <div className="global-order-card-AO__title">Доставка</div>

                <div className="global-order-card-AO__rows">
                    <Row k="Способ доставки" v={order?.deliveryId?.name || order?.deliveryId?.title || order?.deliveryId} />
                    <Row k="Точка самовывоза" v={order?.pickupPointsId?.name || order?.pickupPointsId?.title || order?.pickupPointsId} />
                    <Row k="ТТН / трек-номер" v={order?.packageTracking} />
                </div>
            </div>

            <div className="global-order-card-AO">
                <div className="global-order-card-AO__title">Адрес доставки</div>

                <div className="global-order-card-AO__rows">
                    <Row k="Получатель" v={address?.fullName} />
                    <Row k="Телефон" v={address?.phone} />
                    <Row k="Город" v={address?.city} />
                    <Row
                        k="Адрес"
                        v={[address?.street, address?.house, address?.apartment].filter(Boolean).join(", ")}
                    />
                </div>
            </div>
        </div>
    );
}

function HistoryTab({
    order,
    selectedStatusType,
    setSelectedStatusType,
    onUpdateStatus,
    saving,
    error,
}) {
    return (
        <div className="global-order-history-AO">
            <OrderStatusEditor
                orderDetails={order}
                selectedStatusType={selectedStatusType}
                setSelectedStatusType={setSelectedStatusType}
            />

            {!!error && (
                <div className="order-modal-error-AO">
                    {error}
                </div>
            )}

            <div className="global-order-history-AO__footer">
                <button
                    type="button"
                    className="btn btn--primary"
                    onClick={onUpdateStatus}
                    disabled={saving || !selectedStatusType}
                >
                    {saving ? "Обновляем..." : "Обновить статус"}
                </button>
            </div>
        </div>
    );
}


const TASK_STATUS = {
    published: "Доступно",
    accepted: "Принято",
    in_progress: "В работе",
    review: "На проверке",
    done: "Выполнено",
    rework: "На доработке",
    cancelled: "Отменено",
};

const TASK_PRIORITY = {
    low: "Низкий",
    normal: "Обычный",
    high: "Высокий",
    urgent: "Срочно",
};

function getTaskId(value) {
    if (!value) return "";
    if (typeof value === "string") return value;

    return String(value?._id || value?.id || value?.userId?._id || value?.userId || "");
}

function getTaskUserName(value = {}) {
    const user = value?.userId && typeof value.userId === "object"
        ? value.userId
        : value;

    return [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
        user?.email ||
        user?.username ||
        value?.userName ||
        "Пользователь";
}

function formatTaskDate(value) {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getTaskAssignees(task = {}) {
    const names = (task.assignees || [])
        .map(getTaskUserName)
        .filter(Boolean);

    if (!names.length && task.claimedBy) {
        names.push(getTaskUserName(task.claimedBy));
    }

    if (!names.length && task.assignmentType === "board") {
        return "Свободное задание";
    }

    return names.join(", ") || "Не назначено";
}

function OrderTasksTab({
    tasks,
    loading,
    error,
    onOpenTask,
}) {
    const [expandedId, setExpandedId] = useState("");

    if (loading) {
        return (
            <div className="global-order-empty-AO">
                Загружаем задания заказа...
            </div>
        );
    }

    if (error) {
        return (
            <div className="order-modal-error-AO">
                {error}
            </div>
        );
    }

    if (!tasks.length) {
        return (
            <div className="global-order-empty-AO">
                По этому заказу заданий пока нет.
            </div>
        );
    }

    return (
        <div className="global-order-tasks-AO">
            {tasks.map((task) => {
                const taskId = getTaskId(task);
                const expanded = expandedId === taskId;
                const checklist = Array.isArray(task.checklist) ? task.checklist : [];
                const checklistDone = checklist.filter((item) => item.completed).length;
                const photos = Array.isArray(task.photos) ? task.photos : [];
                const reportCount = photos.filter((photo) => photo.kind === "report").length;
                const sourcePhotoCount = photos.filter((photo) => photo.kind !== "report").length;
                const activity = Array.isArray(task.activity) ? task.activity : [];

                return (
                    <article
                        key={taskId}
                        className={`global-order-task-AO ${expanded ? "is-open" : ""}`}
                    >
                        <button
                            type="button"
                            className="global-order-task-AO__summary"
                            onClick={() => setExpandedId(expanded ? "" : taskId)}
                        >
                            <div className="global-order-task-AO__main">
                                <div className="global-order-task-AO__badges">
                                    <span className={`is-status is-${task.status || "published"}`}>
                                        {TASK_STATUS[task.status] || task.status || "—"}
                                    </span>

                                    <span className={`is-priority is-${task.priority || "normal"}`}>
                                        {TASK_PRIORITY[task.priority] || "Обычный"}
                                    </span>
                                </div>

                                <strong>{task.title || "Без названия"}</strong>

                                <div className="global-order-task-AO__people">
                                    <span>Исполнитель: {getTaskAssignees(task)}</span>
                                    <span>Создал: {getTaskUserName(task.createdBy || {})}</span>
                                </div>
                            </div>

                            <div className="global-order-task-AO__metrics">
                                <span>Чек-лист <strong>{checklistDone}/{checklist.length}</strong></span>
                                <span>Фото <strong>{sourcePhotoCount}</strong></span>
                                <span>Отчёт <strong>{reportCount}</strong></span>
                                <i>{expanded ? "▲" : "▼"}</i>
                            </div>
                        </button>

                        {expanded ? (
                            <div className="global-order-task-AO__details">
                                <div className="global-order-task-AO__facts">
                                    <div>
                                        <span>Создано</span>
                                        <strong>{formatTaskDate(task.createdAt)}</strong>
                                    </div>
                                    <div>
                                        <span>Срок</span>
                                        <strong>{formatTaskDate(task.dueAt)}</strong>
                                    </div>
                                    <div>
                                        <span>Обновлено</span>
                                        <strong>{formatTaskDate(task.updatedAt)}</strong>
                                    </div>
                                    <div>
                                        <span>ID</span>
                                        <strong>#{String(taskId).slice(-6).toUpperCase()}</strong>
                                    </div>
                                </div>

                                {task.description ? (
                                    <div className="global-order-task-AO__description">
                                        {task.description}
                                    </div>
                                ) : null}

                                {checklist.length ? (
                                    <section className="global-order-task-AO__section">
                                        <h4>Чек-лист</h4>

                                        <div className="global-order-task-AO__checklist">
                                            {checklist.map((item) => (
                                                <div
                                                    key={getTaskId(item)}
                                                    className={item.completed ? "is-done" : ""}
                                                >
                                                    <i>{item.completed ? "✓" : "○"}</i>

                                                    <span>
                                                        <strong>{item.text}</strong>

                                                        {item.lastChangedAt ? (
                                                            <small>
                                                                {item.lastChange === "unchecked"
                                                                    ? "Снял отметку"
                                                                    : "Отметил"}{" "}
                                                                {getTaskUserName(item.lastChangedBy || item.completedBy || {})}
                                                                {" · "}
                                                                {formatTaskDate(item.lastChangedAt || item.completedAt)}
                                                            </small>
                                                        ) : item.completedAt ? (
                                                            <small>
                                                                Отметил {getTaskUserName(item.completedBy || {})}
                                                                {" · "}
                                                                {formatTaskDate(item.completedAt)}
                                                            </small>
                                                        ) : null}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                ) : null}

                                <section className="global-order-task-AO__section">
                                    <h4>История действий</h4>

                                    {activity.length ? (
                                        <div className="global-order-task-AO__activity">
                                            {[...activity].reverse().map((item) => (
                                                <div key={getTaskId(item)}>
                                                    <span>{getTaskUserName(item.userId || item)}</span>
                                                    <strong>{item.text || item.type || "Действие"}</strong>
                                                    <small>{formatTaskDate(item.createdAt)}</small>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="global-order-task-AO__empty-line">
                                            История действий пока пустая.
                                        </div>
                                    )}
                                </section>

                                <div className="global-order-task-AO__footer">
                                    <button
                                        type="button"
                                        className="btn btn--primary"
                                        onClick={() => onOpenTask(task)}
                                    >
                                        Открыть задание
                                    </button>
                                </div>
                            </div>
                        ) : null}
                    </article>
                );
            })}
        </div>
    );
}

function FilesTab({ order }) {
    const files = getFiles(order);

    if (!files.length) {
        return (
            <div className="global-order-empty-AO">
                Файлы по заказу пока не найдены.
            </div>
        );
    }

    return (
        <div className="global-order-files-AO">
            {files.map((file, index) => (
                <a
                    key={`${file}-${index}`}
                    className="global-order-files-AO__item"
                    href={generatePublicUrl(file)}
                    target="_blank"
                    rel="noreferrer"
                >
                    Файл {index + 1}
                </a>
            ))}
        </div>
    );
}

export default function GlobalOfficeOrderModal() {
    const dispatch = useDispatch();

    const modalState = useSelector((state) => state.officeOrderModal || {});
    const officeOrderState = useSelector((state) => state.officeOrder || {});
    const officeMemberState = useSelector((state) => state.officeMember || {});
    const authState = useSelector((state) => state.auth || {});

    const {
        open,
        orderId,
        officeId,
        activeTab = "overview",
        sourceTitle = "",
    } = modalState;

    const {
        orderDetails = {},
        detailsLoading,
        actionLoading,
    } = officeOrderState;

    const [portalTarget, setPortalTarget] = useState(null);
    const [selectedStatusType, setSelectedStatusType] = useState("");
    const [localError, setLocalError] = useState("");
    const [orderTasks, setOrderTasks] = useState([]);
    const [orderTasksLoading, setOrderTasksLoading] = useState(false);
    const [orderTasksError, setOrderTasksError] = useState("");

    const safeOrderId = orderId || getOrderId(orderDetails);
    const safeOfficeId = officeId || getOfficeId(orderDetails);
    const currentMember = officeMemberState.currentMemberByOffice?.[safeOfficeId] || null;
    const currentPermissions = officeMemberState.permissionsByOffice?.[safeOfficeId] || currentMember?.permissions || {};
    const canCreateTask = ["owner", "admin", "manager"].includes(currentMember?.role) ||
        !!currentPermissions.employees ||
        !!currentPermissions.settings ||
        ["admin", "superAdmin"].includes(authState.user?.role);
    const canViewTasks = ["owner", "admin"].includes(currentMember?.role) ||
        !!currentPermissions.tasks ||
        ["admin", "superAdmin"].includes(authState.user?.role);

    const isCurrentOrderLoaded = safeOrderId && String(getOrderId(orderDetails)) === String(safeOrderId);

    const order = isCurrentOrderLoaded ? orderDetails : {};
    const orderItemsCount = Array.isArray(order?.items) ? order.items.length : 0;
    const filesCount = getFiles(order).length;
    const orderAmount = getOrderAmount(order);

    const tabs = useMemo(() => [
        { key: "overview", label: "Обзор" },
        { key: "products", label: "Товары", count: orderItemsCount },
        { key: "payment", label: "Оплата" },
        { key: "delivery", label: "Доставка" },
        { key: "history", label: "История" },
        ...(canViewTasks ? [{ key: "tasks", label: "Задания", count: orderTasks.length }] : []),
        { key: "files", label: "Файлы", count: filesCount },
        { key: "finance", label: "Финансы" },
        // Бланк заказа для заказчика: PDF в двух экземплярах и подпись.
        ...(safeOfficeId ? [{ key: "document", label: "Документ" }] : []),
    ], [orderItemsCount, filesCount, canViewTasks, orderTasks.length, safeOfficeId]);

    useEffect(() => {
        setPortalTarget(getPortalTarget());
    }, []);

    useEffect(() => {
        if (!open || !safeOrderId || !safeOfficeId) return;

        dispatch(getOfficeOrderById({
            orderId: safeOrderId,
            officeId: safeOfficeId,
        }));
    }, [dispatch, open, safeOrderId, safeOfficeId]);

    useEffect(() => {
        if (!open || !safeOfficeId) return;
        dispatch(getOfficeMembers(safeOfficeId));
    }, [dispatch, open, safeOfficeId]);

    useEffect(() => {
        if (!open) return undefined;

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                dispatch(closeOfficeOrderModal());
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [dispatch, open]);

    useEffect(() => {
        if (!open) return;

        setSelectedStatusType("");
        setLocalError("");
    }, [open, activeTab, safeOrderId]);

    useEffect(() => {
        if (
            !open ||
            activeTab !== "tasks" ||
            !safeOfficeId ||
            !safeOrderId ||
            !canViewTasks
        ) {
            return undefined;
        }

        let mounted = true;

        setOrderTasksLoading(true);
        setOrderTasksError("");

        axios
            .get(`/office/${safeOfficeId}/tasks`, {
                params: {
                    orderId: safeOrderId,
                },
            })
            .then((response) => {
                if (!mounted) return;

                setOrderTasks(
                    Array.isArray(response.data?.tasks)
                        ? response.data.tasks
                        : []
                );
            })
            .catch((requestError) => {
                if (!mounted) return;

                setOrderTasks([]);
                setOrderTasksError(
                    requestError?.response?.data?.error ||
                    requestError?.response?.data?.message ||
                    requestError?.message ||
                    "Не удалось загрузить задания заказа."
                );
            })
            .finally(() => {
                if (mounted) {
                    setOrderTasksLoading(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [
        open,
        activeTab,
        safeOfficeId,
        safeOrderId,
        canViewTasks,
    ]);

    if (!open || !portalTarget) return null;

    const handleClose = () => {
        dispatch(closeOfficeOrderModal());
    };

    const handleTab = (tab) => {
        setLocalError("");
        dispatch(setOfficeOrderModalTab(tab));
    };

    const handleUpdateStatus = async () => {
        if (!safeOrderId || !safeOfficeId) {
            setLocalError("Не найден ID заказа или офиса.");
            return;
        }

        if (!selectedStatusType) {
            setLocalError("Выбери статус.");
            return;
        }

        const result = await dispatch(updateOfficeOrderStatus({
            orderId: safeOrderId,
            type: selectedStatusType,
            officeId: safeOfficeId,
        }));

        if (!result?.ok) {
            setLocalError(result?.error || result?.message || "Не удалось обновить статус.");
            return;
        }
        emitOfficeAnalyticsRefresh({
            officeId: safeOfficeId,
            orderId: safeOrderId,
            reason: "order_status_updated",
            source: "GlobalOfficeOrderModal",
        });
        setSelectedStatusType("");
        setLocalError("");

        dispatch(getOfficeOrderById({
            orderId: safeOrderId,
            officeId: safeOfficeId,
        }));
    };

    const handleCreateTask = () => {
        if (!safeOrderId || !safeOfficeId) return;

        dispatch(openOfficeTaskCreateModal({
            officeId: safeOfficeId,
            source: "order",
            sourceTitle: `Заказ ${getOrderNumber(order)}`,
            prefill: {
                links: {
                    orderId: safeOrderId,
                    orderNumber: getOrderNumber(order),
                },
            },
        }));
    };

    const handleOpenTask = (task) => {
        const taskId = getTaskId(task);

        if (!taskId || !safeOfficeId) return;

        dispatch(openOfficeEntityModal({
            type: "task",
            entityId: taskId,
            officeId: safeOfficeId,
            source: "order",
            sourceTitle: `Заказ ${getOrderNumber(order)}`,
            payload: task,
        }));
    };

    const handleOpenProduct = (productId, item = {}) => {
        if (!productId || !safeOfficeId) return;

        const product = item?.productId && typeof item.productId === "object"
            ? item.productId
            : {};

        dispatch(openOfficeEntityModal({
            type: "product",
            entityId: productId,
            officeId: safeOfficeId,
            source: "order",
            sourceTitle: `Заказ ${getOrderNumber(order)}`,
            payload: {
                type: "product",
                id: productId,
                officeId: safeOfficeId,
                title: getProductTitle(item),
                image: getProductPicture(product, item),
            },
        }));
    };

    const handleOpenProductGallery = (productId, item = {}) => {
        if (!productId || !safeOfficeId) return;

        const product = item?.productId && typeof item.productId === "object"
            ? item.productId
            : {};

        dispatch(openOfficeProductGallery({
            officeId: safeOfficeId,
            productId,
            title: getProductTitle(item),
            thumbnail: getProductPicture(product, item),
            source: "global-order",
        }));
    };

    const handleOpenProduct3D = (productId, item = {}) => {
        if (!productId || !safeOfficeId || !hasProductModel(item)) return;

        const product = item?.productId && typeof item.productId === "object"
            ? item.productId
            : {};

        dispatch(openOfficeProduct3D({
            officeId: safeOfficeId,
            productId,
            title: getProductTitle(item),
            source: "global-order",
            product: {
                ...product,
                _id: productId,
                fullName: getProductTitle(item),
                price: item?.bayerPrice ?? item?.payablePrice ?? product?.price,
                quantity: item?.purchasedQty ?? product?.quantity,
                height: item?.height ?? product?.height,
                width: item?.width ?? product?.width,
                length: item?.length ?? product?.length,
            },
        }));
    };

    const modalNode = (
        <div className="global-order-modal-AO" role="dialog" aria-modal="true">
            <button
                type="button"
                className="global-order-modal-AO__backdrop"
                onClick={handleClose}
                aria-label="Закрыть"
            />

            <div className="global-order-modal-AO__panel">
                <div className="global-order-modal-AO__head">
                    <div className="global-order-modal-AO__head-main">
                        <div className="global-order-modal-AO__eyebrow">
                            {sourceTitle || "Глобальный просмотр заказа"}
                        </div>

                        <div className="global-order-modal-AO__title">
                            Заказ {isCurrentOrderLoaded ? getOrderNumber(order) : safeOrderId || "—"}
                        </div>

                        <div className="global-order-modal-AO__subtitle">
                            {isCurrentOrderLoaded
                                ? `${formatMoney(orderAmount)} · ${orderItemsCount} поз. · ${formatDate(order?.createdAt)}`
                                : "Загрузка данных заказа..."}
                        </div>
                    </div>

                    <button
                        type="button"
                        className="global-order-modal-AO__close"
                        onClick={handleClose}
                        aria-label="Закрыть"
                    >
                        ×
                    </button>
                </div>

                <div className="global-order-modal-AO__tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={activeTab === tab.key ? "active" : ""}
                            onClick={() => handleTab(tab.key)}
                        >
                            {tab.label}
                            {tab.count !== undefined ? <span>{tab.count}</span> : null}
                        </button>
                    ))}
                </div>

                <div className="global-order-modal-AO__body">
                    {detailsLoading || !isCurrentOrderLoaded ? (
                        <div className="global-order-modal-AO__loading">
                            Загрузка заказа...
                        </div>
                    ) : (
                        <>
                            {activeTab === "overview" && (
                                <OverviewTab order={order} />
                            )}

                            {activeTab === "products" && (
                                <ProductsTab
                                    order={order}
                                    onOpenProduct={handleOpenProduct}
                                    onOpenGallery={handleOpenProductGallery}
                                    onOpen3D={handleOpenProduct3D}
                                />
                            )}

                            {activeTab === "payment" && (
                                <div className="global-order-modal-AO__embedded">
                                    <OrderPaymentEditor
                                        orderDetails={order}
                                        setLocalError={setLocalError}
                                    />
                                </div>
                            )}

                            {activeTab === "delivery" && (
                                <DeliveryTab order={order} />
                            )}
                            {activeTab === "document" && safeOfficeId && safeOrderId && (
                                <div className="global-order-modal-AO__embedded">
                                    <OrderDocumentPanel officeId={safeOfficeId} orderId={safeOrderId} compact />
                                </div>
                            )}
                            {activeTab === "finance" && (
                                <OrderFinanceCalendar
                                    officeId={safeOfficeId}
                                    orderId={safeOrderId}
                                />
                            )}
                            {activeTab === "history" && (
                                <div className="global-order-modal-AO__embedded">
                                    <HistoryTab
                                        order={order}
                                        selectedStatusType={selectedStatusType}
                                        setSelectedStatusType={(nextType) => {
                                            setLocalError("");
                                            setSelectedStatusType(nextType);
                                        }}
                                        onUpdateStatus={handleUpdateStatus}
                                        saving={actionLoading}
                                        error={localError}
                                    />
                                </div>
                            )}

                            {activeTab === "tasks" && canViewTasks && (
                                <OrderTasksTab
                                    tasks={orderTasks}
                                    loading={orderTasksLoading}
                                    error={orderTasksError}
                                    onOpenTask={handleOpenTask}
                                />
                            )}

                            {activeTab === "files" && (
                                <FilesTab order={order} />
                            )}
                        </>
                    )}

                    {!!localError && activeTab !== "history" && (
                        <div className="order-modal-error-AO mt-12">
                            {localError}
                        </div>
                    )}
                </div>

                <div className="global-order-modal-AO__actions">
                    {canCreateTask && isCurrentOrderLoaded ? (
                        <button
                            type="button"
                            className="btn btn--primary"
                            onClick={handleCreateTask}
                        >
                            Создать задание
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className="btn"
                        onClick={handleClose}
                    >
                        Закрыть
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalNode, portalTarget);
}
